import { GalateaPresentationExperimentFlag } from './GalateaFlagsDefinition';
import { OFFER_BUCKET_MAX, offerLayerAllocation, OfferLayerAudience } from './offerLayerAllocation';

export const PAYWALL_UI_BUCKET_MAX = 99;

export type PaywallUiBucketRange = readonly [from: number, to: number];

export type PresentationExperimentClaim = {
  flag: GalateaPresentationExperimentFlag;
  range: PaywallUiBucketRange;
  weights: Record<string, number>;
  languages: readonly string[];
  avoidsStructureChangingOffers?: true;
};

export type PresentationLayerAudienceAllocation = {
  claims: readonly PresentationExperimentClaim[];
};

const videoBackgroundClaim: PresentationExperimentClaim = {
  flag: 'testPaywallVideoBackground',
  range: [0, 19],
  weights: { control: 334, control_aa: 333, variant: 333 },
  languages: ['en'],
};

const socialProofClaim: PresentationExperimentClaim = {
  flag: 'testPaywallSocialProof',
  range: [20, 39],
  weights: { control: 334, social_proof_b: 333, social_proof_c: 333 },
  languages: ['en'],
  avoidsStructureChangingOffers: true,
};

const buttonPulseClaim: PresentationExperimentClaim = {
  flag: 'testPaywallButtonPulse',
  range: [40, 59],
  weights: { control: 334, aa_variant: 333, pulse: 333 },
  languages: ['en'],
};

export const presentationLayerAllocation: Record<OfferLayerAudience, PresentationLayerAudienceAllocation> = {
  US: { claims: [videoBackgroundClaim, socialProofClaim, buttonPulseClaim] },
  DE: { claims: [videoBackgroundClaim, socialProofClaim, buttonPulseClaim] },
  INTL_TIER_0: { claims: [videoBackgroundClaim, socialProofClaim, buttonPulseClaim] },
  OTHER: { claims: [videoBackgroundClaim, socialProofClaim, buttonPulseClaim] },
};

export const presentationClaimShare = ({ range: [from, to] }: PresentationExperimentClaim): number => to - from + 1;

export const presentationArmShare = (claim: PresentationExperimentClaim, arm: string): number =>
  (presentationClaimShare(claim) * (claim.weights[arm] ?? 0)) / 1000;

export const presentationBaselineShare = (audience: OfferLayerAudience): number =>
  PAYWALL_UI_BUCKET_MAX +
  1 -
  presentationLayerAllocation[audience].claims.reduce((sum, claim) => sum + presentationClaimShare(claim), 0);

export const presentationOfferBucketBelow = (
  audience: OfferLayerAudience,
  claim: PresentationExperimentClaim,
): number | undefined => {
  const { presentationSafeBelow } = offerLayerAllocation[audience];

  return claim.avoidsStructureChangingOffers && presentationSafeBelow <= OFFER_BUCKET_MAX
    ? presentationSafeBelow
    : undefined;
};
